import { noop, getPath } from "lib/lodash";
import { secondsInMs, msInSeconds } from "lib/temporal/time";

// handle the video element play promise synchronously to prevent unhandled rejects
export const playVideo = (videoElement, onSuccess = noop, onError = noop) => {
  videoElement.play().then(onSuccess).catch(onError);
};

export const loopTrimmedVideo = (
  videoElement,
  elementData,
  endCallback = noop
) => {
  const {
    startOffset = 0,
    trimDuration,
    duration,
    isLooped = false
  } = elementData;

  // Always use trimDuration if available, otherwise use full duration
  const _trimDuration = trimDuration || duration;
  const endTime = startOffset + _trimDuration;
  const currentTimeInMs = secondsInMs(videoElement?.currentTime);

  // For non-looping videos: stop at end of trim
  if (!isLooped) {
    if (currentTimeInMs >= endTime) {
      videoElement.pause();
      videoElement.currentTime = msInSeconds(startOffset);
      endCallback();
    }
    return;
  }

  // For looping videos: always stay within trimmed section
  if (currentTimeInMs >= endTime || currentTimeInMs < startOffset) {
    // Reset to start of trimmed section
    videoElement.currentTime = msInSeconds(startOffset);
    endCallback();
  }

  if (videoElement.paused) {
    playVideo(videoElement);
  }
};

export const handleTrimmingVideoComponent = ({
  props,
  prevProps,
  trimSource,
  videoRef,
  trimInterval,
  setTrimInterval,
  endCallback,
  forceStartOffset
}) => {
  const { isPlaying, isPaused } = props;
  const { isPlaying: wasPlaying } = prevProps;

  const startOffset = msInSeconds(trimSource.startOffset || 0);
  const videoElement = getPath(videoRef, "current");

  if (!videoElement) {
    return;
  }

  // Delay handling is now managed by the Video component's state
  if (isPlaying && !wasPlaying) {
    videoElement.currentTime = startOffset;
  }

  // Set up looping interval for:
  // 1. Videos with trim (startOffset or trimDuration set)
  // 2. Videos with isLooped=true (need to loop based on duration)
  const needsLoopingInterval =
    trimSource.startOffset ||
    trimSource.trimDuration ||
    (trimSource.isLooped && trimSource.duration);

  if (needsLoopingInterval) {
    if (isPlaying && !wasPlaying) {
      // Set up monitoring interval for both looped and non-looped videos
      // Non-looped videos need this to stop at trimDuration
      setTrimInterval(
        setInterval(() => {
          loopTrimmedVideo(videoElement, trimSource, endCallback);
        }, 1)
      );
    } else if (isPlaying && !trimInterval) {
      // If already playing but no interval exists (e.g., component mounted while playing)
      // Set up the interval now
      setTrimInterval(
        setInterval(() => {
          loopTrimmedVideo(videoElement, trimSource, endCallback);
        }, 1)
      );
    }

    // PHASE 2.5: Don't reset when paused (only when stopped)
    if (!isPlaying && wasPlaying) {
      clearInterval(trimInterval);
      if (forceStartOffset && !isPaused) {
        // Only reset to startOffset when stopped (not paused)
        // When paused, syncVectorToTimeline handles seeking
        videoElement.currentTime = startOffset;
      }
    }
  }

  // PHASE 2.5: Don't reset when paused (only when stopped)
  if (
    forceStartOffset &&
    !isPlaying &&
    !isPaused && // Only reset when stopped (not paused)
    videoElement.currentTime !== startOffset
  ) {
    videoElement.currentTime = startOffset;
  }

  if (
    !forceStartOffset &&
    !isPlaying &&
    !isPaused && // Only reset when stopped (not paused)
    videoElement.currentTime < startOffset
  ) {
    videoElement.currentTime = startOffset;
  }
};
